// spell-components.ts — the browser's share of check_spell_components() (includes/functions/janus/core/check_spell_components.php).
//
// ONE pure mirror, two consumers: the P4 placement gate (ability-preconditions.ts, which refuses with PHP's exact message) and
// the action-bar "can't use" ring (player-character-sheet/ability-button.ts, which shows the short reason on hover). Like the
// rest of P4 it refuses only what the cached rows PROVE PHP refuses — anything it cannot read stands down (returns null) and the
// cast decides. Checks run in PHP's order: verbal → somatic → material. Parity: tools/test/janus-ability-preconditions-parity.test.mjs.

type Row = Record<string, unknown>;

export interface SpellComponentReaders {
  /** layer_has() */
  has: (row: Row | null, key: string) => boolean;
  /** layer_entries() */
  entries: (row: Row | null, key: string) => unknown[];
  /** creature_statuses($row, 'target') */
  statuses: (row: Row) => Row[];
  /** A feat entry → its full catalog def (items + item_layer_types); null when the page does not hold it. */
  resolveItem: (entry: unknown) => Row | null;
}

export type SpellComponentRefusalKind = 'verbal' | 'somatic_restrained' | 'somatic_hands' | 'material_restrained' | 'material_missing';

export interface SpellComponentRefusal {
  kind: SpellComponentRefusalKind;
  /** PHP's exact warning (icon_ph placeholders included). */
  message: string;
  /** The action-bar hover reason. */
  shortReason: string;
}

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;

/** StatusEffect::getId(variableName: …) — includes/catalogs/StatusEffect.php. */
export const STATUS_SILENCED = 'BCC91DAF-AF10-497E-241D-ACD1BEC9E80E';
export const STATUS_SUFFOCATING = '2A20ACDF-3BC6-B964-8FBD-F8E1AC581AA5';
export const STATUS_DROWNING = '1E8008FA-B68A-8DE8-48CE-0EB121008FEB';
export const STATUS_RESTRAINED = 'E34AF232-5438-88FC-9A9F-73850CCCFD51';
/** "No Fingers Required" — the marker passive the somatic gate honours (held directly or feat-granted). */
export const NO_FINGERS_REQUIRED_ID = '92BF9629-9553-DDF4-A189-601EAA1C203C';
/** Cast modes that skip every component check (PHP's "Allow innate or at-will spells" block). */
const COMPONENT_FREE_MODES = ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability'];

/** icon_ph($key) with its default arguments — byte-identical to the PHP placeholder. */
export function iconPh(key: string): string {
  const escaped = key.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  return `<i class='ft-icon' data-icon='${escaped}' data-w='25' data-h='25' style='display:inline-block;width:25px;height:25px' aria-hidden='true'></i>`;
}

const MESSAGES: Record<SpellComponentRefusalKind, () => string> = {
  verbal: () => `You cannot cast spells that require ${iconPh('spell_component_verbal')} verbal components while silenced, suffocating, or drowning.`,
  somatic_restrained: () => `You are restrained, and cannot cast spells that require ${iconPh('spell_component_somatic')} somatic components!`,
  somatic_hands: () => `You must be able to have one hand free to cast spells that require ${iconPh('spell_component_somatic')} somatic components!`,
  material_restrained: () => `You are ${iconPh(STATUS_RESTRAINED)} Restrained, and cannot cast spells that require ${iconPh('spell_component_material')} spell material components!`,
  material_missing: () => `You do not have the ${iconPh('spell_component_material_consumed')} spell material component required to be consumed in or on your inventory or containers, you cannot cast this spell!`,
};
const SHORT_REASONS: Record<SpellComponentRefusalKind, string> = {
  verbal: 'silenced, so no verbal components',
  somatic_restrained: 'restrained, so no somatic components',
  somatic_hands: 'needs a free hand for somatic components',
  material_restrained: 'restrained, so no material components',
  material_missing: 'missing the consumed material component',
};
const refusal = (kind: SpellComponentRefusalKind): SpellComponentRefusal => ({ kind, message: MESSAGES[kind](), shortReason: SHORT_REASONS[kind] });
const escapeHtml = (text: string): string => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

/** has_status_effect() over the target statuses: a row's id is its resolved def's item_id, else the catalog ref it carries. */
export function statusIds(caster: Row, read: Pick<SpellComponentReaders, 'statuses'>): Set<string> {
  const ids = new Set<string>();
  for (const status of read.statuses(caster)) {
    const definition = record(status.status_arr);
    const id = upper(scalar(record(definition?.items)?.item_id) ?? scalar(status.item_id) ?? scalar(status.status_item_id)
      ?? (typeof status.status_arr === 'string' ? status.status_arr : ''));
    if (id) ids.add(id);
  }
  return ids;
}

/** An ability bucket as PHP's layer_entries() bridges it: the compiled computed.creature.abilities row plus any top-level copy. */
export function abilityBucket(caster: Row, key: string, read: Pick<SpellComponentReaders, 'entries'>): unknown[] {
  const compiled = record(record(record(caster.computed)?.creature)?.abilities)?.[key];
  return [...(Array.isArray(compiled) ? compiled : []), ...read.entries(caster, key)];
}

/** layer_list_entry_uuid(): item_layer_effect, then items.item_id, then item_id (each a non-empty string), or the bare
 * string itself — the head of any "uuid|…" packing. Layer-list refs (feat grants, materials) resolve through this. */
export function layerListEntryUuid(entry: unknown): string {
  const head = (value: unknown): string => typeof value === 'string' && value !== '' ? (value.split('|')[0] ?? '') : '';
  const row = record(entry);
  if (!row) return typeof entry === 'string' ? (entry.split('|')[0] ?? '') : '';
  return head(row.item_layer_effect) || head(record(row.items)?.item_id) || head(row.item_id);
}

/** ability_entry_id() for a passive BUCKET row. */
export function entryId(entry: unknown): string {
  if (typeof entry === 'string') return upper(entry);
  const row = record(entry);
  return upper(scalar(row?.ability_id) ?? scalar(record(row?.items)?.item_id) ?? scalar(row?.item_id));
}

/** passive_ability_active_ability_ids(): only array entries count (a bare-string ref is skipped), and a stamped
 * passive_conditions_active must be truthy (passive_ability_entry_is_active). */
export function passiveEntryActive(entry: unknown): boolean {
  const row = record(entry);
  if (!row) return false;
  if (!('passive_conditions_active' in row)) return true;
  const flag = scalar(row.passive_conditions_active);
  return !(flag === undefined || flag === null || flag === false || flag === 0 || flag === '' || flag === '0');
}

/** ft_caster_has_no_fingers_required(): true / false when provable, null when a feat def is not held (cannot prove absence).
 * Feat conditions are not evaluated, so a condition-locked grant reads as held — the gate errs toward letting the cast decide. */
export function casterHasNoFingersRequired(caster: Row, read: SpellComponentReaders): boolean | null {
  return casterHoldsPassive(caster, NO_FINGERS_REQUIRED_ID, read);
}

/** ft_creature_holds_passive_ability(): held in the passive bucket (active), or granted by a held feat. true / false when
 * provable, null when a feat def is not held (cannot prove absence). Feat conditions are not evaluated, so a
 * condition-locked grant reads as held — every caller errs toward letting the cast decide. */
export function casterHoldsPassive(caster: Row, passiveId: string, read: Pick<SpellComponentReaders, 'entries' | 'resolveItem'>): boolean | null {
  const wanted = upper(passiveId);
  if (abilityBucket(caster, 'passive_ability', read).some(entry => entryId(entry) === wanted && passiveEntryActive(entry))) return true;
  let unknown = false;
  for (const featEntry of abilityBucket(caster, 'feat', read)) {
    const nested = record(record(featEntry)?.item_arr);
    const definition = nested && record(nested.item_layer_types) ? nested : read.resolveItem(featEntry);
    if (!definition || !record(definition.item_layer_types)) { unknown = true; continue; }
    if (read.entries(definition, 'passive_ability').some(entry => upper(layerListEntryUuid(entry)) === wanted)) return true;
  }
  return unknown ? null : false;
}

/** inventory_field(): bucket value (first scalar), with the lean inventory row's elided item_qty reading as 1. */
function inventoryField(entry: Row, key: string): unknown {
  const bucket = record(entry.inventory);
  if (bucket && key in bucket) return scalar(bucket[key]);
  const inventoryId = scalar(bucket?.inventory_id);
  if (typeof inventoryId === 'string' && inventoryId !== '' && key === 'item_qty') return 1;
  return null;
}

/** resolve_inventory_layer_list_entry() → [uuid, template, qty>=1]. */
export function materialRequirement(entry: unknown): [string, string, number] {
  let uuid = '';
  let template = '';
  let qty: unknown = null;
  if (record(entry)) {
    const row = entry as Row;
    uuid = layerListEntryUuid(row);
    template = row.template_id != null && row.template_id !== '' ? String(row.template_id) : '';
    qty = row.qty;
  } else {
    const text = String(entry ?? '').trim();
    const [head, tail] = text.includes('|') ? [text.slice(0, text.indexOf('|')), text.slice(text.indexOf('|') + 1)] : [text, null];
    uuid = head;
    if (tail !== null) {
      if (/^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(tail)) qty = Number(tail);
      else if (UUID.test(tail)) template = tail;
    }
  }
  const parsed = Math.trunc(Number(qty ?? 1));
  return [uuid, template, Math.max(1, Number.isFinite(parsed) ? parsed : 0)];
}

/** ft_find_caster_spell_material_entry(): a single stack (recursing carried containers) holding the required qty. */
export function findSpellMaterialStack(items: unknown, uuid: string, template: string, qty: number): Row | null {
  if (!Array.isArray(items)) return null;
  for (const raw of items) {
    const entry = record(raw);
    if (!entry) continue;
    if (String(inventoryField(entry, 'inventory_item_id') ?? '') === uuid
      && String(inventoryField(entry, 'enchantment_template_id') ?? '') === template
      && Math.trunc(Number(inventoryField(entry, 'item_qty') ?? 0)) >= qty) return entry;
    const contents = entry.inventory_in_container ?? entry.container_contents;
    const held = findSpellMaterialStack(contents, uuid, template, qty);
    if (held) return held;
  }
  return null;
}

const inventoryHolds = (items: unknown, uuid: string, template: string, qty: number): boolean =>
  findSpellMaterialStack(items, uuid, template, qty) !== null;

/** Every carried stack (recursing containers), for the missing-material naming below. */
function carriedStacks(items: unknown, out: Row[] = []): Row[] {
  if (!Array.isArray(items)) return out;
  for (const raw of items) {
    const entry = record(raw);
    if (!entry) continue;
    out.push(entry);
    carriedStacks(entry.inventory_in_container ?? entry.container_contents, out);
  }
  return out;
}

/** An enchantment template's display name, read off any carried stack that wears it (the page holds no template catalog). */
function templateName(stacks: Row[], template: string): string {
  const wanted = upper(template);
  for (const stack of stacks) {
    if (upper(inventoryField(stack, 'enchantment_template_id')) !== wanted) continue;
    const name = scalar(record(stack.enchantment_templates)?.enchantment_template_name)
      ?? scalar(record(record(stack.computed)?.enchantment)?.item_name);
    if (typeof name === 'string' && name.trim() !== '') return name.trim();
  }
  return '';
}

/** Names the material the walk stopped on, so the refusal says WHICH component is missing — and, when the caster carries the
 * base item in the wrong version (e.g. an uncut Diamond for a spell that needs "Gemstone: Cut"), says that too. Display only:
 * the verdict itself is PHP's. */
function missingMaterialDetail(caster: Row, uuid: string, template: string, qty: number, read: Pick<SpellComponentReaders, 'resolveItem'>): { html: string; text: string } {
  const definition = read.resolveItem(uuid);
  const stacks = carriedStacks(caster.inventory);
  const held = stacks.filter(stack => String(inventoryField(stack, 'inventory_item_id') ?? '') === uuid);
  // The catalog def names it; failing that, a carried stack of the same item does (its inventory_name).
  const baseName = String(scalar(record(definition?.items)?.item_name) ?? scalar(definition?.item_name) ?? '').trim()
    || held.map(stack => String(inventoryField(stack, 'inventory_name') ?? '').trim()).find(name => name !== '')
    || 'the required item';
  const variant = template ? (templateName(stacks, template) || 'a specific version') : '';
  const label = `${qty > 1 ? `${qty}× ` : ''}${baseName}${variant ? ` (${variant})` : ''}`;
  // The right version carried (in too small a stack) outranks a wrong-version stack as the explanation.
  const rightVersion = held.some(stack => String(inventoryField(stack, 'enchantment_template_id') ?? '') === template);
  const note = rightVersion ? `you carry too few ${baseName} in one stack`
    : held.length > 0 ? `the ${baseName} you carry is not that version` : '';
  return {
    html: `<br />Missing: <b>${escapeHtml(label)}</b>${note ? ` — ${escapeHtml(note)}.` : ''}`,
    text: `missing ${label}${note ? ` (${note})` : ''}`,
  };
}

/** check_spell_components() for one cast: the refusal the cached rows prove PHP would make, else null. */
export function spellComponentRefusal(ability: Row, caster: Row, variable1: unknown, variable2: unknown,
  read: SpellComponentReaders,
  /** R-025 S5: told the site when an APPLICABLE component rule could not be judged (the refusal is then not provable, and the
   * authoritative gate must leave the cast to PHP). Omitted by callers that only want the refusal. */
  onUnknown?: (site: string) => void): SpellComponentRefusal | null {
  const itemType = String(scalar(record(ability.items)?.item_type) ?? scalar(ability.item_type) ?? '');
  if (itemType !== 'spell' && itemType !== 'curse') return null;
  if (!read.has(ability, 'spell_component_verbal') && !read.has(ability, 'spell_component_somatic') && !read.has(ability, 'spell_component_material')) return null;
  if ([variable1, variable2].some(mode => mode !== undefined && mode !== null && COMPONENT_FREE_MODES.includes(String(mode)))) return null;

  const statuses = statusIds(caster, read);
  if (read.has(ability, 'spell_component_verbal') && !read.has(caster, 'ignore_spell_component_verbal')
    && [STATUS_SILENCED, STATUS_SUFFOCATING, STATUS_DROWNING].some(id => statuses.has(id))) {
    return refusal('verbal');
  }

  if (read.has(ability, 'spell_component_somatic') && !read.has(caster, 'ignore_spell_component_somatic')) {
    // Restrained blocks outright — No Fingers Required waives the free hand only, never the bonds.
    if (statuses.has(STATUS_RESTRAINED)) return refusal('somatic_restrained');
    const bypass = casterHasNoFingersRequired(caster, read);
    if (bypass === null) { onUnknown?.('component-somatic-feat'); return null; } // an unreadable feat could be the bypass — the free-hand refusal is not provable
    if (!bypass) {
      // Counts live at computed.creature.equipped on a compiled row, where an absent count IS 0; an uncompiled row proves nothing.
      const compiledCreature = record(record(caster.computed)?.creature);
      const ec = record(caster.encounter_creatures);
      // The PC phase-B cleanup and ft_relocate_creature_derived_to_computed()
      // both elide every zero count, including the whole equipped cluster.
      // On an identified compiled member, absence therefore means free hands.
      // A legacy row with top-level count scratch is left to PHP.
      const leanCompiledMember = compiledCreature && ec?.encounter_creature_id && ec?.encounter_id
        && ['player_character', 'creature'].includes(String(ec?.item_type))
        && ['count_main_hand_equipped', 'count_off_hand_equipped', 'count_both_hands_equipped']
          .every(key => !(key in caster));
      const equipped = record(compiledCreature?.equipped) ?? (leanCompiledMember ? {} : null);
      if (!equipped) { onUnknown?.('component-somatic-hands'); return null; }
      const count = (key: string): number => Number(scalar(equipped[key]) ?? 0) || 0;
      if (count('count_main_hand_equipped') === 1 && count('count_off_hand_equipped') === 1 && count('count_both_hands_equipped') === 0) {
        return refusal('somatic_hands');
      }
    }
  }

  const consumedMaterial = read.has(ability, 'spell_material_item_id') && read.has(ability, 'spell_component_material_consumed');
  // Restrained blocks EVERY material component, consumed or not; only the ignore flag lifts it.
  if ((read.has(ability, 'spell_component_material') || consumedMaterial) && !read.has(caster, 'ignore_spell_component_material')
    && statuses.has(STATUS_RESTRAINED)) {
    return refusal('material_restrained');
  }
  if (consumedMaterial && !read.has(caster, 'ignore_spell_component_material')) {
    if (!Array.isArray(caster.inventory)) { onUnknown?.('component-material-inventory'); return null; } // the page does not hold the caster's inventory
    // PHP's loop verbatim: each entry resets the verdict, a blank entry leaves it false, the first miss stops the walk.
    let found = false;
    let missing: [string, string, number] | null = null;
    for (const entry of read.entries(ability, 'spell_material_item_id')) {
      found = false;
      const [uuid, template, qty] = materialRequirement(entry);
      if (uuid === '') continue;
      if (inventoryHolds(caster.inventory, uuid, template, qty)) found = true;
      if (!found) { missing = [uuid, template, qty]; break; }
    }
    if (!found) {
      const base = refusal('material_missing');
      if (!missing) return base;
      // PHP's message stays the prefix (the parity oracle checks it); the named detail is the browser's addition.
      const detail = missingMaterialDetail(caster, missing[0], missing[1], missing[2], read);
      return { ...base, message: base.message + detail.html, shortReason: detail.text };
    }
  }
  return null;
}
